<div class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
  <!-- Modern Page Header -->
  <div class="bg-white shadow-sm border-b border-slate-200">
    <div class="max-w-7xl mx-auto px-6 py-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-slate-900 mb-2">Operations Ready for Audit</h1>
          <p class="text-slate-600 flex items-center gap-2">
            <span nz-icon nzType="info-circle" class="text-blue-500"></span>
            <ng-container *ngIf="canOverrideAuditOrder(); else chronologicalIntro">
              Review completed operations — oldest first is recommended, not required for your role
            </ng-container>
            <ng-template #chronologicalIntro>
              Review and audit completed operations in chronological order
            </ng-template>
          </p>
        </div>
        <button
          (click)="refresh()"
          class="btn btn-primary btn-md gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          [class.loading]="operationsQuery.isRefetching()"
        >
          <span nz-icon nzType="reload" [nzSpin]="operationsQuery.isRefetching()"></span>
          Refresh
        </button>
      </div>
    </div>
  </div>

  <div class="max-w-7xl mx-auto px-6 py-8">
    <!--
      Audits in progress.

      Lists EVERY open audit, not just one. Oversight roles may now hold several, and an
      open audit that nothing on screen names is one that goes missing — which is exactly
      how a single stalled audit disabled this whole page for days. Each row carries its
      own Resume and Release, so the way out is on the page where the problem is felt.
    -->
    <div *ngIf="hasAuditInProgress()" class="alert alert-warning shadow-xl mb-6 border-2 border-orange-300">
      <div class="flex items-start gap-4 w-full">
        <div class="flex-shrink-0">
          <div class="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
            <span nz-icon nzType="exclamation-circle" nzTheme="fill" class="text-white text-xl"></span>
          </div>
        </div>
        <div class="flex-1">
          <h3 class="font-bold text-lg text-orange-900 mb-1">
            {{ auditsInProgress().length === 1 ? 'You Have an Audit in Progress' : 'You Have ' + auditsInProgress().length + ' Audits in Progress' }}
          </h3>
          <p class="text-sm text-orange-800 mb-3">
            {{ blockedByOpenAudit()
              ? 'You must complete or release this audit before starting another.'
              : 'You can start another at any time — these stay open until you finish or release them.' }}
          </p>

          <div class="space-y-2">
            <div
              *ngFor="let openAudit of auditsInProgress()"
              class="flex flex-wrap items-center gap-3 rounded-lg bg-base-100/70 px-3 py-2"
            >
              <span class="badge badge-warning font-mono">{{ openAudit.auditNumber }}</span>
              <span class="font-semibold text-orange-900 flex-1 min-w-0 truncate">{{ openAudit.routeName }}</span>
              <span class="text-xs text-orange-800">Ready {{ openAudit.auditReadyAt | amTimeAgo }}</span>
              <div class="flex gap-2 flex-shrink-0">
                <button
                  class="btn btn-primary btn-xs gap-1"
                  [routerLink]="['/audits', openAudit.id, 'detail']"
                >
                  <span nz-icon nzType="play-circle" nzTheme="fill"></span>
                  Resume
                </button>
                <button
                  class="btn btn-error btn-outline btn-xs gap-1"
                  (click)="releaseAudit(openAudit)"
                  [class.loading]="cancelAuditMutation.isPending()"
                >
                  <span nz-icon nzType="close-circle"></span>
                  Release
                </button>
              </div>
            </div>
          </div>

          <button
            class="btn btn-warning btn-sm gap-2 mt-3 shadow-lg hover:shadow-xl transition-all duration-300"
            [routerLink]="['/audits/my-audits']"
          >
            <span nz-icon nzType="eye"></span>
            View My Audits
          </button>
        </div>
      </div>
    </div>

    <!-- FIFO Information Card -->
    <div *ngIf="shouldShowAuditPolicy()" class="alert alert-info shadow-lg mb-8 border border-blue-200">
      <div class="flex items-start gap-4">
        <div class="flex-shrink-0">
          <div class="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
            <span nz-icon nzType="ordered-list" class="text-white text-xl"></span>
          </div>
        </div>
        <div class="flex-1">
          <h3 class="font-bold text-lg text-blue-900 mb-1">Audit Order Policy</h3>
          <p class="text-blue-800" *ngIf="!canOverrideAuditOrder()">
            Operations must be audited in order of completion (oldest first). 
            Please start with the operation marked <span class="badge badge-success badge-sm">⚡ Audit This First</span>.
          </p>
          <p class="text-blue-800" *ngIf="canOverrideAuditOrder()">
            Auditors must work oldest first, starting with
            <span class="badge badge-success badge-sm">⚡ Oldest Waiting</span>. As a
            {{ roleLabel() }} you may start any operation — out-of-order starts are recorded against your
            name and shown on the trip audit report.
          </p>
        </div>
      </div>
    </div>

    <!-- Trips whose footage is still arriving: not auditable, and not openable (#805 Phase 3). -->
    <buspro-admin-collecting-operations-panel />

    <!-- Loading State with Modern Skeleton -->
    <div *ngIf="operationsQuery.isPending()" class="space-y-4">
      <div class="card bg-white shadow-xl animate-pulse">
        <div class="card-body">
          <div class="h-8 bg-slate-200 rounded w-1/4 mb-4"></div>
          <div class="space-y-3">
            <div class="h-16 bg-slate-200 rounded"></div>
            <div class="h-16 bg-slate-200 rounded"></div>
            <div class="h-16 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State with Modern Card -->
    <div *ngIf="operationsQuery.isError()">
      <div class="card bg-white shadow-2xl border-2 border-red-200">
        <div class="card-body text-center py-16">
          <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span nz-icon nzType="exclamation-circle" class="text-5xl text-red-500"></span>
          </div>
          <h3 class="text-2xl font-bold text-slate-900 mb-3">
            Failed to Load Operations
          </h3>
          <p class="text-slate-600 mb-8 max-w-md mx-auto">
            There was an error loading operations ready for audit. Please check your connection and try again.
          </p>
          <button
            (click)="refresh()"
            class="btn btn-error gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <span nz-icon nzType="reload"></span>
            Try Again
          </button>
        </div>
      </div>
    </div>

    <!-- Success State with Modern Cards -->
    <div *ngIf="operationsQuery.isSuccess() && operationsQuery.data() as operations">
      <!-- Modern Operation Cards -->
      <div *ngIf="operations.length > 0; else emptyState" class="space-y-4">
        <div
          *ngFor="let operation of operations; let i = index"
          class="card bg-white shadow-lg hover:shadow-2xl transition-all duration-300 border-l-4"
          [ngClass]="{
            'border-green-500 ring-2 ring-green-200 bg-gradient-to-r from-green-50 to-white': isOldestOperation(operation),
            'border-slate-300': !isOldestOperation(operation)
          }"
        >
          <div class="card-body p-6">
            <!-- Compact Card Layout -->
            <div class="flex items-center gap-4">
              <!-- Audit Number Badge -->
              <div class="relative flex-shrink-0">
                <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span class="text-white font-bold text-sm">{{ operation.auditNumber }}</span>
                </div>
                <div
                  *ngIf="isOldestOperation(operation)"
                  class="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-bounce"
                >
                  <span nz-icon nzType="thunderbolt" class="text-white text-xs"></span>
                </div>
              </div>

              <!-- Operation Info (Flex-grow to take available space) -->
              <div class="flex-1 min-w-0">
                <!-- Route Name and Priority Badge -->
                <div class="flex items-center gap-2 mb-2">
                  <h3 class="text-lg font-bold text-slate-900 truncate">{{ operation.routeName }}</h3>
                  <span
                    *ngIf="isOldestOperation(operation)"
                    class="badge badge-success badge-sm gap-1 shadow-md animate-pulse flex-shrink-0"
                  >
                    <span nz-icon nzType="fire" nzTheme="fill"></span>
                    {{ canOverrideAuditOrder() ? 'Oldest Waiting' : 'Audit This First' }}
                  </span>
                  <span
                    *ngIf="!isOldestOperation(operation) && canOverrideAuditOrder()"
                    class="badge badge-ghost badge-sm gap-1 flex-shrink-0"
                    nz-tooltip
                    nzTooltipTitle="Starting this one is recorded as an out-of-order audit"
                  >
                    <span nz-icon nzType="swap"></span>
                    {{ olderWaitingCount(operation) }} ahead
                  </span>
                </div>
                
                <!-- Metadata Row: Time, Date, Bus -->
                <div class="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                  <div class="flex items-center gap-1">
                    <span nz-icon nzType="clock-circle" class="text-blue-500"></span>
                    <span>Ready {{ operation.auditReadyAt | amTimeAgo }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <span nz-icon nzType="calendar" class="text-slate-400"></span>
                    <span>{{ operation.auditReadyAt | date: 'short' }}</span>
                  </div>
                  <div *ngxPermissionsOnly="adminRoles" class="flex items-center gap-1">
                    <span nz-icon nzType="car" class="text-green-500"></span>
                    <span class="font-medium">{{ operation.busName || 'N/A' }}</span>
                  </div>
                </div>
              </div>

              <!-- Status Badge -->
              <div class="flex-shrink-0">
                <div class="badge badge-lg gap-2" 
                     [ngClass]="{
                       'badge-success': operation.auditStatus === 'Ready',
                       'badge-info': operation.auditStatus !== 'Ready'
                     }">
                  <span nz-icon nzType="check-circle" nzTheme="fill"></span>
                  {{ operation.auditStatus }}
                </div>
              </div>

              <!-- Action Button -->
              <div class="flex-shrink-0">
                <button
                  class="btn btn-primary btn-md gap-2 shadow-lg transition-all duration-300"
                  [ngClass]="{
                    'hover:scale-105 hover:shadow-xl': canStartAudit(operation),
                    'btn-disabled opacity-50 cursor-not-allowed': !canStartAudit(operation)
                  }"
                  [disabled]="!canStartAudit(operation)"
                  [nz-tooltip]="getTooltip(operation)"
                  [class.loading]="startAuditMutation.isPending()"
                  (click)="startAudit(operation.id)"
                >
                  <span nz-icon nzType="play-circle" nzTheme="fill"></span>
                  <span class="font-semibold">Start Audit</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modern Empty State -->
      <ng-template #emptyState>
        <div class="card bg-white shadow-2xl">
          <div class="card-body text-center py-20">
            <div class="w-32 h-32 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <span nz-icon nzType="check-circle" nzTheme="fill" class="text-7xl text-green-600"></span>
            </div>
            <h2 class="text-3xl font-bold text-slate-900 mb-4">
              All Caught Up! 🎉
            </h2>
            <p class="text-lg text-slate-600 mb-2 max-w-md mx-auto">
              No operations are currently waiting for audit.
            </p>
            <p class="text-sm text-slate-500 max-w-md mx-auto">
              New operations will appear here when they complete and become ready for review.
            </p>
            
            <div class="mt-8 flex justify-center gap-4">
              <div class="stats shadow-lg">
                <div class="stat place-items-center">
                  <div class="stat-title text-slate-600">Status</div>
                  <div class="stat-value text-green-600 text-2xl">All Clear</div>
                  <div class="stat-desc text-slate-500">No pending audits</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-template>
    </div>
  </div>

  <!--
    Out-of-order confirmation.

    An Admin/Manager skipping the queue is allowed but never silent: the dialog states
    how many older operations are being jumped and that the start is recorded, and offers
    a reason box. Optional on purpose — a mandatory reason only breeds "n/a".
  -->
  <div *ngIf="overrideCandidate() as candidate" class="modal modal-open">
    <div class="modal-box max-w-lg bg-base-100 text-base-content">
      <h3 class="flex items-center gap-2 text-lg font-bold">
        <span nz-icon nzType="swap" class="text-warning"></span>
        Start out of order?
      </h3>

      <p class="mt-3 text-sm text-base-content/70">
        <span class="badge badge-outline badge-sm font-mono">{{ candidate.auditNumber }}</span>
        {{ candidate.routeName }} is not the oldest operation waiting. Starting it now jumps
        <span class="font-semibold text-base-content">
          {{ olderWaitingCount(candidate) }} older operation{{ olderWaitingCount(candidate) === 1 ? '' : 's' }}</span>.
      </p>

      <p class="mt-2 text-sm text-base-content/70">
        This is recorded against your name and appears on the trip audit report. Auditors cannot do this.
      </p>

      <label class="form-control mt-4 w-full">
        <span class="label-text text-sm text-base-content/70">Reason (optional)</span>
        <textarea
          rows="3"
          maxlength="500"
          class="textarea textarea-bordered mt-1 w-full"
          placeholder="e.g. passenger complaint on this trip, conductor waiting on a shortfall"
          [ngModel]="overrideReason()"
          (ngModelChange)="overrideReason.set($event)"
        ></textarea>
      </label>

      <div class="modal-action">
        <button class="btn btn-ghost" (click)="cancelOverride()">Cancel</button>
        <button class="btn btn-warning gap-2" (click)="confirmOverride()">
          <span nz-icon nzType="play-circle" nzTheme="fill"></span>
          Start and record
        </button>
      </div>
    </div>
    <div class="modal-backdrop bg-base-300/60" (click)="cancelOverride()"></div>
  </div>
</div>
